import { ApiError, authFetch } from "@/lib/api-client";
import {
  AMBER_MAX_BACKOFF_MULTIPLIER,
  OPTIONS_POLL_INTERVAL_MS,
  OPTIONS_POLL_TIMEOUT_MS,
} from "@/lib/query-constants";

/**
 * Shape the `/{scope}/status/:correlationId` routes return for an options job.
 * `data` carries the payload the worker put on the correlation status; it is absent
 * for kinds whose result is too large to round-trip through Redis (see
 * `rereadOnComplete`).
 */
interface AmberOptionsStatus<T> {
  status: "ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED";
  data?: T | null;
  error?: string;
  message?: string;
}

interface FetchAmberOptionsArgs {
  signal?: AbortSignal;
  /** Poll cadence override. Defaults to `OPTIONS_POLL_INTERVAL_MS`. */
  intervalMs?: number;
  /**
   * Treat a COMPLETED status as a signal to re-read `url` — by then a warm cache
   * hit — instead of reading the payload off the status envelope. For results too
   * large to duplicate into the correlation status value.
   */
  rereadOnComplete?: boolean;
}

export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const id = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Resolves an options endpoint that the workspace worker backs: a warm cache hit
 * answers `200 { data }` inline, a cold miss answers `202 { correlationId }` and the
 * result lands on `{statusPath}/status/:correlationId`.
 *
 * Every terminal failure throws rather than resolving empty: a FAILED job, an exhausted
 * poll budget, or a COMPLETED status carrying no payload. Any of them resolving `[]` or
 * `null` would render as an empty dropdown with no error surfaced anywhere, which is
 * exactly the regression this indirection risks.
 * Callers must pair this with `retry: false` — a thrown FAILED is a settled job, not
 * a transient 5xx worth retrying.
 */
export async function fetchAmberOptions<T>(
  url: string,
  statusPath: string,
  { signal, intervalMs = OPTIONS_POLL_INTERVAL_MS, rereadOnComplete = false }: FetchAmberOptionsArgs = {},
): Promise<T> {
  const first = await authFetch<T>(url, { signal });
  if (first.data != null) return first.data;

  if (!first.correlationId) {
    throw new ApiError(
      502,
      `${url} returned neither data nor a correlationId`,
      "MISSING_CORRELATION_ID",
    );
  }

  const deadline = Date.now() + OPTIONS_POLL_TIMEOUT_MS;
  const maxDelayMs = intervalMs * AMBER_MAX_BACKOFF_MULTIPLIER;
  // Never reset on a successful poll: under a rate limit, alternating 429 and PROCESSING
  // would otherwise drop straight back to the base cadence.
  let delayMs = intervalMs;
  for (;;) {
    let status: AmberOptionsStatus<T> | null;
    try {
      ({ data: status } = await authFetch<AmberOptionsStatus<T>>(
        `${statusPath}/status/${first.correlationId}`,
        { signal },
      ));
    } catch (err) {
      if (!isTransientPollError(err)) throw err;
      delayMs = Math.min(delayMs * 2, maxDelayMs);
      await sleepWithinBudget(delayMs, deadline, url, signal);
      continue;
    }

    if (status?.status === "COMPLETED") {
      if (!rereadOnComplete) {
        // A COMPLETED job with no payload is a backend regression, not an empty result —
        // an empty result still arrives as `[]` or `{}`. Resolving `null as T` here would
        // hand callers a value their type forbids and surface as a blank dropdown.
        if (status.data == null) {
          throw new ApiError(
            502,
            `${url} completed without a result payload`,
            "OPTIONS_MISSING_PAYLOAD",
          );
        }
        return status.data;
      }

      const reread = await authFetch<T>(url, { signal });
      if (reread.data != null) return reread.data;
      throw new ApiError(
        502,
        `${url} completed but the cached result was already gone`,
        "OPTIONS_REREAD_EMPTY",
      );
    }

    if (status?.status === "FAILED") {
      throw new ApiError(
        500,
        status.error ?? status.message ?? `${url} failed`,
        "JOB_FAILED",
      );
    }

    // Checked after the request, not before the sleep, so a job that completes on
    // the very last allowed poll still resolves.
    if (Date.now() > deadline) {
      throw pollTimeoutError(url);
    }

    await sleepWithinBudget(delayMs, deadline, url, signal);
  }
}

/** Sleeps for `ms`, but never past `deadline`, so a long backoff cannot overrun the budget. */
async function sleepWithinBudget(
  ms: number,
  deadline: number,
  url: string,
  signal?: AbortSignal,
): Promise<void> {
  const remainingMs = deadline - Date.now();
  if (remainingMs <= 0) throw pollTimeoutError(url);
  await abortableSleep(Math.min(ms, remainingMs), signal);
}

const TRANSIENT_POLL_ERROR_CODES = new Set(["NETWORK_ERROR", "TIMEOUT", "OFFLINE"]);

/**
 * The edge rate limiter answers 429 without CORS headers, so a cross-origin browser
 * sees it as a network error, and enough of them flip the app to OFFLINE. In all of
 * these, and on a single slow poll, the job is still running; slow down rather than
 * fail or keep hammering. OFFLINE never reaches the network, so waiting it out is free.
 */
function isTransientPollError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.status === 429 || (err.code !== undefined && TRANSIENT_POLL_ERROR_CODES.has(err.code)))
  );
}

function pollTimeoutError(url: string): ApiError {
  return new ApiError(504, `${url} timed out`, "POLL_TIMEOUT");
}
